import { useCallback, useEffect, useRef, useState } from 'react'

import { isAxiosError } from 'axios'

import { isDuplicatePaymentMethod } from '@b-stock/fe-core/bstock-react'
import type { PayMethod } from '@b-stock/payments-methods-api-client'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'

type Params = {
  scopeKey?: string
  existingMethods: PayMethod[]
  fetchMethods: () => Promise<PayMethod[]>
  deleteMethod: (id: string) => Promise<PayMethod>
  onSuccess: (method: PayMethod) => void | Promise<void>
  onDuplicate?: () => void | Promise<void>
  onTimeout?: () => void | Promise<void>
  pollConfig?: {
    maxRetries?: number
    retryDelay?: number
    initialDelay?: number
  }
}

export function useHandleBalanceCCAdded({
  scopeKey = '',
  existingMethods,
  fetchMethods,
  deleteMethod,
  onSuccess,
  onDuplicate,
  onTimeout,
  pollConfig = {},
}: Params) {
  const [isProcessing, setIsProcessing] = useState(false)
  const lifecycle = useRef({ mounted: true, generation: 0 })
  const finishDelay = useRef<(() => void) | null>(null)
  const scope = useRef(scopeKey)
  scope.current = scopeKey // oxlint-disable-line react/refs
  const cancelDelay = useCallback(() => {
    finishDelay.current?.()
  }, [])
  useEffect(() => {
    const life = lifecycle.current
    life.mounted = true
    setIsProcessing(false) // oxlint-disable-line react/set-state-in-effect
    return () => {
      life.mounted = false
      life.generation++
      cancelDelay()
    }
  }, [scopeKey, cancelDelay])
  const { maxRetries = 20, retryDelay = 1000, initialDelay = 0 } = pollConfig
  const handleBalanceCCAdded = useCallback(
    async (paymentMethodId: string) => {
      cancelDelay()
      const life = lifecycle.current
      const run = ++life.generation
      const current = () =>
        life.mounted && life.generation === run && scope.current === scopeKey
      if (!current()) return
      if (!paymentMethodId) {
        await onTimeout?.()
        return
      }
      setIsProcessing(true)
      const delay = (ms: number) =>
        new Promise<void>((resolve) => {
          const finish = () => {
            clearTimeout(timer)
            finishDelay.current = null
            resolve()
          }
          const timer = setTimeout(finish, ms)
          finishDelay.current = finish
        })
      try {
        if (initialDelay) await delay(initialDelay)
        for (let attempt = 0; attempt < maxRetries && current(); attempt++) {
          let methods: PayMethod[]
          try {
            methods = await fetchMethods()
          } catch (error) {
            if (!current()) return
            if (
              (isAxiosError(error) &&
                error.response &&
                error.response.status < 500) ||
              attempt === maxRetries - 1
            )
              throw error
            await delay(retryDelay)
            continue
          }
          if (!current()) return
          const method = methods.find(
            (m) =>
              m.type === PayMethodTypeEnum.GetbalanceCc &&
              m.status === 'reusable' &&
              m.details.id === paymentMethodId
          )
          if (method) {
            if (
              isDuplicatePaymentMethod(
                method,
                existingMethods.filter((m) => m.id !== method.id)
              )
            ) {
              await deleteMethod(method.id)
              if (current()) await onDuplicate?.()
            } else {
              await onSuccess(method)
            }
            return
          }
          if (attempt < maxRetries - 1) await delay(retryDelay)
        }
        if (current()) await onTimeout?.()
      } catch {
        if (current()) await onTimeout?.()
      } finally {
        if (current()) setIsProcessing(false)
      }
    },
    [
      existingMethods,
      fetchMethods,
      deleteMethod,
      onSuccess,
      onDuplicate,
      onTimeout,
      maxRetries,
      retryDelay,
      initialDelay,
      scopeKey,
      cancelDelay,
    ]
  )
  return { handleBalanceCCAdded, isProcessing }
}
